// Zentraler Totara-Bereich: Liste aller dokumentierten Einstellungen.
//
// Globale Einstellungen, Kursset-Einstellungen (Standardmandanten bzw.
// Sülzle, Weinmann Aach, Carl Spaeter) und mandantenspezifische Einträge
// leben bewusst in derselben Liste – unterschieden über die Spalte
// "Geltung" und die Filter. So bleibt alles Totara-Bezogene an einer
// Stelle, statt über die Mandanten-Detailseiten verstreut zu sein.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api.js';
import TotaraSettingFormModal from './TotaraSettingFormModal.jsx';
import { formatScope } from './ScopeSelect.jsx';
import { courseSetLabel } from '../courseSets.js';

export default function Totara() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Filter als URL-Query-Parameter: bookmarkbar und überlebt Re-Renders.
    const scope = searchParams.get('scope') || '';
    const courseSetFilter = searchParams.get('kursset') || '';
    const mandantFilter = searchParams.get('mandant') || '';
    const categoryFilter = searchParams.get('kategorie') || '';

    const [settings, setSettings] = useState([]);
    const [mandanten, setMandanten] = useState([]);
    const [categories, setCategories] = useState([]);
    const [courseSets, setCourseSets] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);

    // Geltungsfilter, Kursset und konkreter Mandant schließen sich
    // gegenseitig aus – beim Setzen des einen werden die anderen geleert.
    const EXCLUSIVE = ['scope', 'kursset', 'mandant'];

    function setParam(key, value) {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (value) next.set(key, value);
                else next.delete(key);
                if (value && EXCLUSIVE.includes(key)) {
                    EXCLUSIVE.filter((k) => k !== key).forEach((k) => next.delete(k));
                }
                return next;
            },
            { replace: true },
        );
    }

    async function reload() {
        try {
            setError('');
            const rows = await api.listTotaraSettings({
                search,
                scope,
                course_set: courseSetFilter,
                mandant_id: mandantFilter,
                category: categoryFilter,
            });
            setSettings(rows);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    // Stammdaten für die Dropdowns einmalig laden.
    useEffect(() => {
        api.listMandanten().then(setMandanten).catch(() => { /* unkritisch */ });
        api.listTotaraCategories().then(setCategories).catch(() => { /* unkritisch */ });
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
    }, []);

    // Debounced reload bei Such- oder Filteränderung.
    useEffect(() => {
        const t = setTimeout(reload, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, scope, courseSetFilter, mandantFilter, categoryFilter]);

    async function handleDelete(id) {
        if (!window.confirm('Diese Einstellung wirklich löschen?')) return;
        try {
            await api.deleteTotaraSetting(id);
            await reload();
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;

    return (
        <div>
            <h1>Totara</h1>
            <p className="muted" style={{ marginTop: -8 }}>
                Dokumentierte Einstellungen der Totara-Instanz – global, je Kursset
                oder für einen einzelnen Mandanten.
            </p>

            {error && (
                <div className="panel" style={{ color: 'var(--color-danger)' }}>
                    Fehler: {error}
                </div>
            )}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Suche in Bezeichnung, Wert, Beschreibung…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 300 }}
                />
                <select
                    value={categoryFilter}
                    onChange={(e) => setParam('kategorie', e.target.value)}
                >
                    <option value="">Alle Kategorien</option>
                    {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
                <select value={scope} onChange={(e) => setParam('scope', e.target.value)}>
                    <option value="">Alle Geltungsbereiche</option>
                    <option value="global">Nur global</option>
                    <option value="kursset">Nur Kursset-bezogen</option>
                    <option value="mandant">Nur mandantenspezifisch</option>
                </select>
                <select
                    value={courseSetFilter}
                    onChange={(e) => setParam('kursset', e.target.value)}
                >
                    <option value="">Alle Kurssets</option>
                    {courseSets.map((cs) => (
                        <option key={cs} value={cs}>
                            {courseSetLabel(cs)}
                        </option>
                    ))}
                </select>
                <select
                    value={mandantFilter}
                    onChange={(e) => setParam('mandant', e.target.value)}
                >
                    <option value="">Alle Mandanten</option>
                    {mandanten.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>
                <div className="spacer" />
                <button className="btn btn--primary" onClick={() => setCreating(true)}>
                    Neue Einstellung
                </button>
            </div>

            <div className="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Kategorie</th>
                            <th>Bezeichnung</th>
                            <th>Wert</th>
                            <th>Geltung</th>
                            <th>Zuletzt geändert</th>
                            <th style={{ width: 1 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {settings.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="empty">
                                    Keine Einstellungen gefunden.
                                </td>
                            </tr>
                        ) : settings.map((s) => (
                            <tr
                                key={s.id}
                                className="row-clickable"
                                onClick={() => navigate(`/totara/${s.id}`)}
                            >
                                <td>{s.category}</td>
                                <td>{s.name}</td>
                                <td style={{ maxWidth: 300, whiteSpace: 'pre-wrap' }}>
                                    {s.value || <span className="muted">–</span>}
                                </td>
                                <td>
                                    {s.mandant_id || s.course_set
                                        ? formatScope(s)
                                        : <span className="muted">global</span>}
                                </td>
                                <td>{formatDate(s.updated_at || s.created_at)}</td>
                                <td>
                                    <button
                                        className="btn btn--sm btn--danger"
                                        onClick={(ev) => {
                                            ev.stopPropagation();
                                            handleDelete(s.id);
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <TotaraSettingFormModal
                    mandanten={mandanten}
                    categories={categories}
                    courseSets={courseSets}
                    onClose={() => setCreating(false)}
                    onSaved={async () => {
                        setCreating(false);
                        // Kategorien neu laden – evtl. wurde eine neue vergeben.
                        api.listTotaraCategories().then(setCategories).catch(() => {});
                        await reload();
                    }}
                />
            )}
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
